<template>
  <b-sidebar
    right
    shadow
    backdrop
    width="min(420px, 100vw)"
    :visible="!!item"
    :title="item ? item.display_name : ''"
    data-test="file-drawer"
    @change="onChange"
  >
    <div v-if="item" class="drawer-body">
      <component
        :is="attachment ? 'button' : 'div'"
        :type="attachment ? 'button' : null"
        :class="['drawer-preview', { 'drawer-preview-link': attachment }]"
        :aria-label="attachment ? 'View full size' : null"
        data-test="drawer-preview"
        @click="attachment && (showFull = true)"
      >
        <img
          v-if="previewUrl && !imageFailed"
          :src="previewUrl"
          :alt="item.display_name"
          class="drawer-image"
          data-test="drawer-image"
          @error="imageFailed = true"
        />
        <file-thumb-preview
          v-else-if="canThumb"
          :key="attachment.id"
          :file="attachment"
          :kind="kind"
          :width="thumbWidth"
          @failed="thumbFailed = true"
        />
        <div v-else :class="['drawer-tile', `kind-${kind}`]">
          {{ kindLabel }}
        </div>
      </component>

      <div v-if="attachmentCount > 1" class="drawer-pager">
        <button
          type="button"
          class="drawer-pager-btn"
          aria-label="Previous attachment"
          :disabled="index === 0"
          data-test="drawer-prev"
          @click="index--"
        >
          <b-icon icon="chevron-left" />
        </button>
        <span class="drawer-pages" data-test="drawer-pages">
          {{ index + 1 }} of {{ attachmentCount }}
        </span>
        <button
          type="button"
          class="drawer-pager-btn"
          aria-label="Next attachment"
          :disabled="index === attachmentCount - 1"
          data-test="drawer-next"
          @click="index++"
        >
          <b-icon icon="chevron-right" />
        </button>
      </div>

      <dl class="drawer-details">
        <dt>Type</dt>
        <dd>{{ kindLabel }}</dd>
        <dt>{{ ownerLabel }}</dt>
        <dd>{{ item.owner }}</dd>
        <dt>{{ creatorLabel }}</dt>
        <dd>{{ item.created_by }}</dd>
        <dt>Added</dt>
        <dd>{{ addedText }}</dd>
        <template v-if="item.expiry_date || item.expiry">
          <dt>Expiry</dt>
          <dd data-test="drawer-expiry">
            {{ item.expiry_date ? item.expiry_date.format('D MMM YYYY') : '' }}
            <span
              v-if="item.expiry"
              :class="['drawer-expiry', `expiry-${item.expiry}`]"
            >
              {{ expiryLabel }}
            </span>
          </dd>
        </template>
        <template v-if="folderLabels.length">
          <dt>Folders</dt>
          <dd>{{ folderLabels.join(', ') }}</dd>
        </template>
      </dl>

      <div class="drawer-actions">
        <button
          type="button"
          class="drawer-action drawer-action-primary"
          data-test="drawer-open"
          @click="$emit('open-full', item)"
        >
          <b-icon icon="box-arrow-up-right" />
          Open file
        </button>
        <button
          v-if="canDownload"
          type="button"
          class="drawer-action"
          data-test="drawer-download"
          @click="$emit('download', item.file)"
        >
          <b-icon icon="download" />
          Download
        </button>
        <button
          v-if="canShare"
          type="button"
          class="drawer-action"
          data-test="drawer-share"
          @click="$emit('share', item.file)"
        >
          <b-icon icon="cursor" />
          Share
        </button>
        <button
          v-if="canEdit"
          type="button"
          class="drawer-action"
          data-test="drawer-edit"
          @click="$emit('edit', item.file)"
        >
          <b-icon icon="pencil-square" />
          Edit
        </button>
        <button
          v-if="canDelete"
          type="button"
          class="drawer-action drawer-action-danger"
          data-test="drawer-delete"
          @click="$emit('delete', item.file)"
        >
          <b-icon icon="trash" />
          Delete
        </button>
      </div>
      <file-preview-modal
        v-if="showFull && attachment"
        visible
        :url="attachment.url"
        :mime-type="attachment.mime_type"
        :name="attachment.file_name"
        :media-id="attachment.id"
        :download-name="item.display_name"
        :allow-download="canDownload"
        @close="showFull = false"
      />
    </div>
  </b-sidebar>
</template>

<script>
import FileThumbPreview from '@/components/files/FileThumbPreview'
import FilePreviewModal from '@/components/modals/FilePreviewModal'
import { EXPIRY_LABELS } from '@/utils/fileExpiry'
import { fileKind } from '@/utils/fileTypeIcons'

const KIND_NAMES = {
  pdf: 'PDF',
  doc: 'Word document',
  xls: 'Spreadsheet',
  img: 'Image',
}

export default {
  components: { FilePreviewModal, FileThumbPreview },

  props: {
    item: { type: Object, default: null },
    folderLabels: { type: Array, default: () => [] },
    ownerLabel: { type: String, default: 'Owner' },
    creatorLabel: { type: String, default: 'Creator' },
    canDownload: { type: Boolean, default: false },
    canShare: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
  },

  data() {
    return { index: 0, imageFailed: false, showFull: false, thumbFailed: false }
  },

  computed: {
    attachments() {
      return (this.item && this.item.file && this.item.file.files) || []
    },

    attachmentCount() {
      return this.attachments.length
    },

    attachment() {
      return this.attachments[this.index] || null
    },

    // The type of the attachment being viewed, not of the file's first one.
    kind() {
      const kind = this.attachment ? fileKind(this.attachment) : 'other'
      // An attachment with no name or type to go on takes the file's type.
      return kind === 'other' && this.index === 0 ? this.item.kind : kind
    },

    // A PDF's thumbnail is its rendered first page; images fall back to the file itself.
    previewUrl() {
      const attachment = this.attachment
      if (!attachment) return ''
      if (attachment.thumb_url) return attachment.thumb_url
      return this.kind === 'img' ? attachment.url : ''
    },

    // PDFs, Word documents and spreadsheets get a drawn preview when the server sent none.
    canThumb() {
      return (
        !!this.attachment &&
        !this.thumbFailed &&
        ['pdf', 'doc', 'xls'].includes(this.kind)
      )
    },

    thumbWidth() {
      return 300
    },

    kindLabel() {
      return (this.item && KIND_NAMES[this.kind]) || 'File'
    },

    expiryLabel() {
      return this.item ? EXPIRY_LABELS[this.item.expiry] : ''
    },

    addedText() {
      const added = this.item && this.item.added_at
      return added
        ? `${this.item.added} (${added.toLocaleDateString('en-AU')})`
        : '-'
    },
  },

  watch: {
    item() {
      this.index = 0
      this.imageFailed = false
      this.thumbFailed = false
      this.showFull = false
    },

    index() {
      this.imageFailed = false
      this.thumbFailed = false
    },
  },

  methods: {
    onChange(visible) {
      if (!visible) this.$emit('close')
    },
  },
}
</script>

<style scoped>
.drawer-body {
  padding: 4px 20px 24px;
}

.drawer-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.drawer-image {
  max-width: 100%;
  max-height: 320px;
  border-radius: 6px;
}

.drawer-tile {
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  border-radius: 14px;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 18px;
  font-weight: 900;
}

.kind-pdf {
  background-color: #fdecec;
  color: #b42318;
}

.kind-doc {
  background-color: #e8f1ff;
  color: #0b55c9;
}

.kind-xls {
  background-color: #e6f6ec;
  color: #14703a;
}

.kind-img {
  background-color: #f1ebfb;
  color: #6b3fb0;
}

.drawer-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -8px 0 20px;
}

.drawer-pages {
  min-width: 64px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
}

.drawer-pager-btn {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
}

.drawer-pager-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.drawer-pager-btn:disabled {
  opacity: 0.4;
}

.drawer-details {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 8px 12px;
  margin-bottom: 24px;
  font-size: 14px;
}

.drawer-details dt {
  color: #6b7280;
  font-weight: 700;
}

.drawer-details dd {
  margin: 0;
  color: #111827;
  overflow-wrap: anywhere;
}

.drawer-expiry {
  margin-left: 6px;
  padding: 1px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.expiry-expired {
  background-color: #fdecec;
  color: #b42318;
}

.expiry-expiring {
  background-color: #fff4e0;
  color: #9a5b00;
}

.drawer-actions {
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
}

.drawer-preview-link {
  width: 100%;
  cursor: zoom-in;
}

.drawer-preview-link:hover {
  border-color: #006cff;
}

.drawer-action-primary,
.drawer-action-primary svg {
  color: #006cff;
}

.drawer-action {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
}

.drawer-action svg {
  margin-right: 12px;
  color: #6b7280;
}

.drawer-action:hover {
  background-color: #f3f4f6;
}

.drawer-action-danger,
.drawer-action-danger svg {
  color: #b42318;
}

.drawer-action-danger:hover {
  background-color: #fdecec;
}
</style>
